import React, { useState, useEffect } from 'react';

// Add initialDimensions prop to receive saved dimensions from parent
const QuiltSetup = ({ onDimensionsChange, onGridSizeChange, defaultGridSize, disabled = false, initialDimensions = null }) => {
    // State for dimensions with null initial values to indicate "not selected"
    const [quiltWidth, setQuiltWidth] = useState(null);
    const [quiltHeight, setQuiltHeight] = useState(null);
    const [tileWidth, setTileWidth] = useState(null);
    const [tileHeight, setTileHeight] = useState(null);
    
    // Input field values (what user sees and enters in the inputs)
    const [quiltWidthInput, setQuiltWidthInput] = useState('');
    const [quiltHeightInput, setQuiltHeightInput] = useState('');
    const [tileWidthInput, setTileWidthInput] = useState('');
    const [tileHeightInput, setTileHeightInput] = useState('');
    
    // Calculated grid size
    const [gridSize, setGridSize] = useState(defaultGridSize || '');
    const [totalTiles, setTotalTiles] = useState(0);
    
    // Tracking if dimensions were initially loaded from saved values
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    
    // Warning for large quilts
    const [sizeWarning, setSizeWarning] = useState('');
    
    // Input validation errors
    const [errors, setErrors] = useState({
        quiltWidth: '',
        quiltHeight: '',
        tileWidth: '',
        tileHeight: ''
    });

    // Limits for quilt and tile sizes
    const quiltSizeLimits = { min: 4, max: 8 }; // feet - updated max from 9 to 8
    const tileSizeLimits = { min: 6, max: 15 }; // inches - updated min from 8 to 6

    // First useEffect to handle initialDimensions (saved custom dimensions)
    useEffect(() => {
        // Only apply initialDimensions if they exist
        if (initialDimensions && 
            initialDimensions.quiltWidth && 
            initialDimensions.quiltHeight && 
            initialDimensions.tileWidth && 
            initialDimensions.tileHeight) {
            
            console.log("Restoring saved dimensions:", initialDimensions);
            
            // Restore all dimension values from initialDimensions
            setQuiltWidth(initialDimensions.quiltWidth);
            setQuiltHeight(initialDimensions.quiltHeight);
            setTileWidth(initialDimensions.tileWidth);
            setTileHeight(initialDimensions.tileHeight);
            
            // Also update input fields to match
            setQuiltWidthInput(initialDimensions.quiltWidth.toString());
            setQuiltHeightInput(initialDimensions.quiltHeight.toString());
            setTileWidthInput(initialDimensions.tileWidth.toString());
            setTileHeightInput(initialDimensions.tileHeight.toString());
            
            // Mark initial load as complete to prevent overrides
            setInitialLoadComplete(true);
        }
    }, [initialDimensions]); // Only depend on initialDimensions

    // Add this useEffect to react to initialDimensions changes, specifically for reset
    useEffect(() => {
        // Check if this is a reset operation (all values null or dimensions object is null)
        const isReset = !initialDimensions || 
                       (initialDimensions.quiltWidth === null && 
                        initialDimensions.quiltHeight === null && 
                        initialDimensions.tileWidth === null && 
                        initialDimensions.tileHeight === null) ||
                       initialDimensions.totalTiles === 0;
        
        if (isReset) {
            console.log("Reset detected in QuiltSetup, clearing all fields");
            
            // Reset all state values to null
            setQuiltWidth(null);
            setQuiltHeight(null);
            setTileWidth(null);
            setTileHeight(null);
            
            // Clear all input field values
            setQuiltWidthInput('');
            setQuiltHeightInput('');
            setTileWidthInput('');
            setTileHeightInput('');
            
            // Reset grid size and calculations
            setGridSize('');
            setTotalTiles(0);
            setSizeWarning('');
            
            // Clear validation errors
            setErrors({
                quiltWidth: '',
                quiltHeight: '',
                tileWidth: '',
                tileHeight: ''
            });
            
            // Mark initial load as complete to prevent default values from being applied
            setInitialLoadComplete(true);
            
            // Reset input fields to empty directly
            setTimeout(() => {
                const inputs = document.querySelectorAll('input[name^="quilt"], input[name^="tile"]');
                inputs.forEach(input => {
                    input.value = '';
                    
                    // Dispatch events to ensure React state is updated
                    const inputEvent = new Event('input', { bubbles: true });
                    const changeEvent = new Event('change', { bubbles: true });
                    input.dispatchEvent(inputEvent);
                    input.dispatchEvent(changeEvent);
                });
            }, 50);
            
            return;
        }
        
        // Handle normal initialization with valid values (existing code)
        if (initialDimensions && 
            initialDimensions.quiltWidth && 
            initialDimensions.quiltHeight && 
            initialDimensions.tileWidth && 
            initialDimensions.tileHeight) {
            
            console.log("Restoring saved dimensions:", initialDimensions);
            
            // Restore all dimension values from initialDimensions
            setQuiltWidth(initialDimensions.quiltWidth);
            setQuiltHeight(initialDimensions.quiltHeight);
            setTileWidth(initialDimensions.tileWidth);
            setTileHeight(initialDimensions.tileHeight);
            
            // Update input fields to match
            setQuiltWidthInput(initialDimensions.quiltWidth.toString());
            setQuiltHeightInput(initialDimensions.quiltHeight.toString());
            setTileWidthInput(initialDimensions.tileWidth.toString());
            setTileHeightInput(initialDimensions.tileHeight.toString());
            
            // Mark initial load as complete
            setInitialLoadComplete(true);
        }
    }, [initialDimensions]);

    // Modified useEffect to handle defaultGridSize changes
    // This now respects already loaded values
    useEffect(() => {
        console.log("defaultGridSize changed to:", defaultGridSize);
        
        if (!defaultGridSize || defaultGridSize === '') {
            // Reset all values when defaultGridSize is empty/null, but only if no custom dimensions
            if (!initialLoadComplete && !initialDimensions) {
                setQuiltWidth(null);
                setQuiltHeight(null);
                setTileWidth(null);
                setTileHeight(null);
                setQuiltWidthInput('');
                setQuiltHeightInput('');
                setTileWidthInput('');
                setTileHeightInput('');
                setGridSize('');
                setTotalTiles(0);
                setSizeWarning('');
                setErrors({
                    quiltWidth: '',
                    quiltHeight: '',
                    tileWidth: '',
                    tileHeight: ''
                });
            }
        } 
        else if (defaultGridSize.includes('x')) {
            // Only set defaults if we have a valid grid size
            const [cols, rows] = defaultGridSize.split('x').map(Number);
            if (!isNaN(cols) && !isNaN(rows) && cols >= 4 && rows >= 4) {
                // We set defaults only if:
                // 1. The fields are currently empty
                // 2. We haven't already loaded initial dimensions
                // 3. No initialDimensions were provided
                const shouldSetDefaults = 
                    !initialLoadComplete &&
                    quiltWidth === null && quiltWidthInput === '' &&
                    quiltHeight === null && quiltHeightInput === '' &&
                    tileWidth === null && tileWidthInput === '' &&
                    tileHeight === null && tileHeightInput === '' &&
                    (!initialDimensions || 
                     !initialDimensions.quiltWidth || 
                     !initialDimensions.quiltHeight);
                
                if (shouldSetDefaults) {
                    console.log("Setting default dimensions for grid size:", defaultGridSize);
                    // Set default values
                    setQuiltWidth(4);
                    setQuiltHeight(4);
                    setTileWidth(12);
                    setTileHeight(12);
                    
                    // Update input fields to match
                    setQuiltWidthInput('4');
                    setQuiltHeightInput('4');
                    setTileWidthInput('12');
                    setTileHeightInput('12');
                }
            }
        }
    }, [defaultGridSize, initialLoadComplete, quiltWidth, quiltHeight, tileWidth, tileHeight, 
        quiltWidthInput, quiltHeightInput, tileWidthInput, tileHeightInput, initialDimensions]);

    // Calculate grid size whenever these core values change
    useEffect(() => {
        const calculateGridSize = () => {
            // Only calculate if all dimensions are valid
            if (quiltWidth && quiltHeight && tileWidth && tileHeight) {
                // Convert feet to inches
                const quiltWidthInches = quiltWidth * 12;
                const quiltHeightInches = quiltHeight * 12;
                
                // Calculate exact number of tiles that fit
                const columns = Math.floor(quiltWidthInches / tileWidth);
                const rows = Math.floor(quiltHeightInches / tileHeight);
                
                // Calculate leftover space (remainder)
                const widthRemainder = quiltWidthInches % tileWidth;
                const heightRemainder = quiltHeightInches % tileHeight;
                
                // Create warning message if needed
                let warningMessage = '';
                if (widthRemainder > 0 || heightRemainder > 0) {
                    warningMessage = `Warning: Your layout will have unused space (${widthRemainder.toFixed(1)} inches on width, ${heightRemainder.toFixed(1)} inches on height). Consider using tile sizes that divide evenly, like 6, 8, or 12 inches, to avoid gaps.`; 
                }
                
                // Enforce minimum grid size of 4x4
                const finalColumns = Math.max(columns, 4);
                const finalRows = Math.max(rows, 4);
                
                // Check for tile limit
                const maxTiles = 100;
                const calculatedTotalTiles = finalColumns * finalRows;
                
                if (calculatedTotalTiles > maxTiles) {
                    warningMessage = `Warning: Your quilt will require ${calculatedTotalTiles} tiles, which exceeds our recommended limit of ${maxTiles} tiles. Consider using larger tiles.`; 
                }
                
                // Set warning message
                setSizeWarning(warningMessage);
                
                // Create new grid size string
                const newGridSize = `${finalColumns}x${finalRows}`;
                
                // Calculate pixel dimensions for tiles
                // Always set height to exactly 300 pixels
                const FIXED_HEIGHT_PIXELS = 300;
                
                // For tiles with equal width and height (square tiles), use exact 300x300
                let tileWidthPixels, tileHeightPixels;
                
                if (tileWidth === tileHeight) {
                    // If square tile (1:1 aspect ratio), use 300x300 pixels
                    tileWidthPixels = 300;
                    tileHeightPixels = 300;
                    console.log(`Square tile detected (${tileWidth}x${tileHeight}). Using 300x300 pixels.`);
                } else {
                    // For non-square tiles, maintain aspect ratio with fixed height
                    tileHeightPixels = FIXED_HEIGHT_PIXELS;
                    const aspectRatio = tileWidth / tileHeight;
                    tileWidthPixels = Math.round(tileHeightPixels * aspectRatio);
                    console.log(`Rectangular tile (${tileWidth}x${tileHeight}). Using ${tileWidthPixels}x${tileHeightPixels} pixels (aspect ratio: ${aspectRatio.toFixed(2)}).`);
                }
                
                // Only update if grid size has changed
                if (newGridSize !== gridSize) {
                    setGridSize(newGridSize);
                    setTotalTiles(calculatedTotalTiles);
                    
                    // Notify parent components
                    if (onGridSizeChange) {
                        onGridSizeChange(newGridSize);
                    }
                    
                    if (onDimensionsChange) {
                        onDimensionsChange({
                            quiltWidth,
                            quiltHeight,
                            tileWidth,
                            tileHeight,
                            columns: finalColumns,
                            rows: finalRows,
                            totalTiles: calculatedTotalTiles,
                            tileWidthPixels,
                            tileHeightPixels,
                            leftoverWidth: widthRemainder,
                            leftoverHeight: heightRemainder
                        });
                    }
                }
            } else {
                // Not all dimensions are set, so reset calculated values
                if (gridSize !== '') {
                    setGridSize('');
                    setTotalTiles(0);
                    setSizeWarning('');
                    
                    // Notify parent components about reset
                    if (onGridSizeChange) {
                        onGridSizeChange('');
                    }
                    
                    if (onDimensionsChange) {
                        onDimensionsChange({
                            quiltWidth: null,
                            quiltHeight: null,
                            tileWidth: null,
                            tileHeight: null,
                            columns: 0,
                            rows: 0,
                            totalTiles: 0,
                            tileWidthPixels: 0,
                            tileHeightPixels: 0
                        });
                    }
                }
            }
        };
        
        // Call the calculation function
        calculateGridSize();
    }, [quiltWidth, quiltHeight, tileWidth, tileHeight, onDimensionsChange, onGridSizeChange, gridSize]);

    // Validate the quilt width input
    const validateQuiltWidth = (value) => {
        if (value === '') return { valid: false, error: 'Width is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        if (numericValue < quiltSizeLimits.min) 
            return { valid: false, error: `Min ${quiltSizeLimits.min} feet` };
        if (numericValue > quiltSizeLimits.max) 
            return { valid: false, error: `Max ${quiltSizeLimits.max} feet` };
        
        // Check if width is greater than height (if height is set)
        if (quiltHeight && numericValue > quiltHeight) 
            return { valid: false, error: `Width can't exceed height` };
            
        return { valid: true, error: '', value: numericValue };
    };
    
    // Validate the quilt height input
    const validateQuiltHeight = (value) => {
        if (value === '') return { valid: false, error: 'Height is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        if (numericValue < quiltSizeLimits.min) 
            return { valid: false, error: `Min ${quiltSizeLimits.min} feet` };
        if (numericValue > quiltSizeLimits.max) 
            return { valid: false, error: `Max ${quiltSizeLimits.max} feet` };
        
        return { valid: true, error: '', value: numericValue };
    };
    
    // Validate the tile width input
    const validateTileWidth = (value) => {
        if (value === '') return { valid: false, error: 'Width is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        // Dynamic minimum tile size based on quilt height
        let dynamicMinTileSize = tileSizeLimits.min; // Default minimum (6 inches)
        
        if (quiltHeight) {
            // For 8 feet height, require at least 8 inch tiles
            if (quiltHeight >= 8) {
                dynamicMinTileSize = 8;
            }
            // For 7 feet height, require at least 7 inch tiles
            else if (quiltHeight >= 7) {
                dynamicMinTileSize = 7;
            }
            // For smaller heights, we can use the default minimum (6 inches)
        }
        
        if (numericValue < dynamicMinTileSize) 
            return { valid: false, error: `Min ${dynamicMinTileSize} inches for ${quiltHeight}' height` };
        if (numericValue > tileSizeLimits.max) 
            return { valid: false, error: `Max ${tileSizeLimits.max} inches` };
        
        // Ensure tile size can't be set too large (would result in grid less than 4x4)
        const maxWidth = quiltWidth ? (quiltWidth * 12) / 4 : 24;
        if (numericValue > maxWidth) 
            return { valid: false, error: `Too large for quilt width` };
        
        // Check if width is larger than height (if height is set)
        if (tileHeight && numericValue > tileHeight) 
            return { valid: false, error: `Width can't exceed height` };
        
        return { valid: true, error: '', value: numericValue };
    };
    
    // Validate the tile height input
    const validateTileHeight = (value) => {
        if (value === '') return { valid: false, error: 'Height is required' };
        
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return { valid: false, error: 'Must be a number' };
        
        // Dynamic minimum tile size based on quilt height
        let dynamicMinTileSize = tileSizeLimits.min; // Default minimum (6 inches)
        
        if (quiltHeight) {
            // For 8 feet height, require at least 8 inch tiles
            if (quiltHeight >= 8) {
                dynamicMinTileSize = 8;
            }
            // For 7 feet height, require at least 7 inch tiles
            else if (quiltHeight >= 7) {
                dynamicMinTileSize = 7;
            }
            // For smaller heights, we can use the default minimum (6 inches)
        }
        
        if (numericValue < dynamicMinTileSize) 
            return { valid: false, error: `Min ${dynamicMinTileSize} inches for ${quiltHeight}' height` };
        if (numericValue > tileSizeLimits.max) 
            return { valid: false, error: `Max ${tileSizeLimits.max} inches` };
        
        // Ensure tile size can't be set too large (would result in grid less than 4x4)
        const maxHeight = quiltHeight ? (quiltHeight * 12) / 4 : 24;
        if (numericValue > maxHeight) 
            return { valid: false, error: `Too large for quilt height` };
        
        return { valid: true, error: '', value: numericValue };
    };

    // Add a useEffect to revalidate tile sizes when quilt height changes
    useEffect(() => {
        // Only run if quilt height and tile dimensions are set
        if (quiltHeight && (tileWidth || tileWidthInput) && (tileHeight || tileHeightInput)) {
            // Get dynamic minimum based on quilt height
            let dynamicMinTileSize = tileSizeLimits.min;
            if (quiltHeight >= 8) {
                dynamicMinTileSize = 8;
            } else if (quiltHeight >= 7) {
                dynamicMinTileSize = 7;
            }
            
            // Revalidate tile width
            if (tileWidth && tileWidth < dynamicMinTileSize) {
                setErrors(prev => ({
                    ...prev,
                    tileWidth: `Min ${dynamicMinTileSize} inches for ${quiltHeight}' height`
                }));
                // Don't clear the value - let user adjust it
            }
            
            // Revalidate tile height
            if (tileHeight && tileHeight < dynamicMinTileSize) {
                setErrors(prev => ({
                    ...prev,
                    tileHeight: `Min ${dynamicMinTileSize} inches for ${quiltHeight}' height`
                }));
                // Don't clear the value - let user adjust it
            }
        }
    }, [quiltHeight, tileWidth, tileHeight, tileWidthInput, tileHeightInput]);

    // Input handler for quilt width - add revalidation of height
    const handleQuiltWidthChange = (e) => {
        const inputValue = e.target.value;
        setQuiltWidthInput(inputValue);
        
        // Handle empty input
        if (!inputValue) {
            setQuiltWidth(null);
            setErrors(prev => ({ ...prev, quiltWidth: 'Width is required' }));
            
            // Clear height error related to width constraint
            if (errors.quiltHeight === 'Width can\'t exceed height') {
                setErrors(prev => ({ ...prev, quiltHeight: '' }));
            }
            return;
        }
        
        // Validate the input
        const validation = validateQuiltWidth(inputValue);
        setErrors(prev => ({ ...prev, quiltWidth: validation.error }));
        
        // Only update state if valid
        if (validation.valid) {
            const newWidth = validation.value;
            setQuiltWidth(newWidth);
            
            // Re-validate height to ensure width is not greater than height
            if (quiltHeight && newWidth > quiltHeight) {
                setErrors(prev => ({ ...prev, quiltWidth: 'Width can\'t exceed height' }));
                // Don't reset width value - let user adjust it
            }
        } else {
            setQuiltWidth(null);
        }
    };

    // Input handler for quilt height - update to revalidate width
    const handleQuiltHeightChange = (e) => {
        const inputValue = e.target.value;
        setQuiltHeightInput(inputValue);
        
        // Handle empty input
        if (!inputValue) {
            setQuiltHeight(null);
            setErrors(prev => ({ ...prev, quiltHeight: 'Height is required' }));
            return;
        }
        
        // Validate the input
        const validation = validateQuiltHeight(inputValue);
        setErrors(prev => ({ ...prev, quiltHeight: validation.error }));
        
        // Only update state if valid
        if (validation.valid) {
            const newHeight = validation.value;
            setQuiltHeight(newHeight);
            
            // Re-validate width to ensure it's not greater than the new height
            if (quiltWidth && quiltWidth > newHeight) {
                setErrors(prev => ({ ...prev, quiltWidth: 'Width can\'t exceed height' }));
                // Don't reset width value - let user adjust it
            } else {
                // Clear any width errors related to height constraint
                if (errors.quiltWidth === 'Width can\'t exceed height') {
                    setErrors(prev => ({ ...prev, quiltWidth: '' }));
                }
            }
        } else {
            setQuiltHeight(null);
        }
    };

    // Input handler for tile width
    const handleTileWidthChange = (e) => {
        const inputValue = e.target.value;
        setTileWidthInput(inputValue);
        
        // Handle empty input
        if (!inputValue) {
            setTileWidth(null);
            setErrors(prev => ({ ...prev, tileWidth: 'Width is required' }));
            return;
        }
        
        // Validate the input
        const validation = validateTileWidth(inputValue);
        setErrors(prev => ({ ...prev, tileWidth: validation.error }));
        
        // Only update state if valid
        if (validation.valid) {
            setTileWidth(validation.value);
        } else {
            setTileWidth(null);
        }
    };

    // Input handler for tile height - add revalidation of width
    const handleTileHeightChange = (e) => {
        const inputValue = e.target.value;
        setTileHeightInput(inputValue);
        
        // Handle empty input
        if (!inputValue) {
            setTileHeight(null);
            setErrors(prev => ({ ...prev, tileHeight: 'Height is required' }));
            
            // Clear width error related to height constraint
            if (errors.tileWidth === 'Width can\'t exceed height') {
                setErrors(prev => ({ ...prev, tileWidth: '' }));
            }
            return;
        }
        
        // Validate the input
        const validation = validateTileHeight(inputValue);
        setErrors(prev => ({ ...prev, tileHeight: validation.error }));
        
        // Only update state if valid
        if (validation.valid) {
            const newHeight = validation.value;
            setTileHeight(newHeight);
            
            // Re-validate width to ensure it's not greater than the new height
            if (tileWidth && parseFloat(tileWidth) > parseFloat(newHeight)) {
                setErrors(prev => ({ ...prev, tileWidth: 'Width can\'t exceed height' }));
                // Don't reset width value - let user adjust it
            }
        } else {
            setTileHeight(null);
        }
    };

    // We're removing the tryUpdateDimensions helper function as it causes additional state updates
    // It's better to let the main useEffect handle the calculations based on state changes

    const validateDimensions = (dimensions) => {
        const errors = {};
        
        // Other validations...
        
        // Fix the width/height comparison to allow equal values
        if (parseFloat(dimensions.tileWidth) > parseFloat(dimensions.tileHeight)) {
          errors.tileWidth = "Width can't exceed height";
        }
        
        // Other validations...
        
        return errors;
    };

    // Inside the component, ensure all inputs respect disabled state
    return (
        <div className="bg-white p-4 rounded-lg shadow-md">
            <h2 className={`text-lg font-semibold mb-4 ${disabled ? 'text-gray-500' : 'text-gray-700'}`} style={{ fontFamily: "Playfair Display" }}>
                Quilt Layout Calculator
            </h2>
            
            {/* First box: Quilt Dimensions */}
            <div className="mb-6">
                <div className="bg-[#F0F4ED] p-4 rounded-lg border border-[#DCE8DB]">
                    <h3 className="text-lg font-semibold text-[#7A8B73] mb-3">
                        Quilt Dimensions
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        {/* Quilt Width */}
                        <div className="flex flex-col">
                            <label className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-gray-700'} mb-1`} style={{ fontFamily: "Playfair Display" }}>
                                Quilt Width<br />(feet)
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="quiltWidth"
                                    value={quiltWidthInput}
                                    onChange={handleQuiltWidthChange}
                                    placeholder={`${quiltSizeLimits.min}-${quiltSizeLimits.max} feet`}
                                    className={`border rounded-md p-2 text-gray-800 w-full ${errors.quiltWidth ? 'border-red-500' : 'border-[#9CAF88]'} ${disabled ? 'bg-gray-100 text-gray-500' : ''}`}
                                    disabled={disabled}
                                />
                                {errors.quiltWidth && (
                                    <p className="text-red-500 text-xs mt-1">{errors.quiltWidth}</p>
                                )}
                            </div>
                        </div>
                        
                        {/* Quilt Height */}
                        <div className="flex flex-col">
                            <label className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-gray-700'} mb-1`} style={{ fontFamily: "Playfair Display" }}>
                                Quilt Height<br />(feet)
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="quiltHeight"
                                    value={quiltHeightInput}
                                    onChange={handleQuiltHeightChange}
                                    placeholder={`${quiltSizeLimits.min}-${quiltSizeLimits.max} feet`}
                                    className={`border rounded-md p-2 text-gray-800 w-full ${errors.quiltHeight ? 'border-red-500' : 'border-[#9CAF88]'} ${disabled ? 'bg-gray-100 text-gray-500' : ''}`}
                                    disabled={disabled}
                                />
                                {errors.quiltHeight && (
                                    <p className="text-red-500 text-xs mt-1">{errors.quiltHeight}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Second box: Tile Dimensions */}
            <div className="mb-6">
                <div className="bg-[#F0F4ED] p-4 rounded-lg border border-[#DCE8DB]">
                    <h3 className="text-lg font-semibold text-[#7A8B73] mb-3">
                        Tile Dimensions
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        {/* Tile Width */}
                        <div className="flex flex-col">
                            <label className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-gray-700'} mb-1`} style={{ fontFamily: "Playfair Display" }}>
                                Tile Width<br />(inches)
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="tileWidth"
                                    value={tileWidthInput}
                                    onChange={handleTileWidthChange}
                                    placeholder={`${tileSizeLimits.min}-${tileSizeLimits.max} inches`}
                                    className={`border rounded-md p-2 text-gray-800 w-full ${errors.tileWidth ? 'border-red-500' : 'border-[#9CAF88]'} ${disabled ? 'bg-gray-100 text-gray-500' : ''}`}
                                    disabled={disabled}
                                />
                                {errors.tileWidth && (
                                    <p className="text-red-500 text-xs mt-1">{errors.tileWidth}</p>
                                )}
                            </div>
                        </div>
                        
                        {/* Tile Height */}
                        <div className="flex flex-col">
                            <label className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-gray-700'} mb-1`} style={{ fontFamily: "Playfair Display" }}>
                                Tile Height<br />(inches)
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="tileHeight"
                                    value={tileHeightInput}
                                    onChange={handleTileHeightChange}
                                    placeholder={`${tileSizeLimits.min}-${tileSizeLimits.max} inches`}
                                    className={`border rounded-md p-2 text-gray-800 w-full ${errors.tileHeight ? 'border-red-500' : 'border-[#9CAF88]'} ${disabled ? 'bg-gray-100 text-gray-500' : ''}`}
                                    disabled={disabled}
                                />
                                {errors.tileHeight && (
                                    <p className="text-red-500 text-xs mt-1">{errors.tileHeight}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Enhanced Results */}
            <div className={`${disabled ? 'bg-gray-100' : 'bg-[#F8F9FA] border border-[#9CAF88]'} p-3 rounded-md`}>
                <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                        Grid Size:
                    </span>
                    <span className={`text-sm font-bold ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                        {gridSize ? `${gridSize} (${totalTiles} T-shirts)` : "Please enter all dimensions"}
                    </span>
                </div>
                
                <div className="flex justify-between items-center mt-2">
                    <span className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                        Final Quilt Size:
                    </span>
                    <span className={`text-sm ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                        {quiltWidth && quiltHeight ? `${quiltWidth} × ${quiltHeight} ft` : "Not determined"}
                    </span>
                </div>
                
                {gridSize && (
                    <>
                        <div className="flex justify-between items-center mt-2">
                            <span className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                                Tiles Required:
                            </span>
                            <span className={`text-sm ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                                {totalTiles} tiles ({gridSize.split('x')[0]} columns × {gridSize.split('x')[1]} rows)
                            </span>
                        </div>
                        
                        {(quiltWidth * 12) % tileWidth > 0 || (quiltHeight * 12) % tileHeight > 0 ? (
                            <div className="flex justify-between items-center mt-2">
                                <span className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                                    Leftover Space:
                                </span>
                                <span className={`text-sm ${disabled ? 'text-gray-500' : 'text-[#8B6F47]'}`} style={{ fontFamily: "Playfair Display" }}>
                                    Width: {((quiltWidth * 12) % tileWidth).toFixed(1)}″, Height: {((quiltHeight * 12) % tileHeight).toFixed(1)}″
                                </span>
                            </div>
                        ) : (
                            <div className={`mt-2 text-sm ${disabled ? 'text-gray-500' : 'text-[#7A8B73]'}`} style={{ fontFamily: "Playfair Display" }}>
                                Perfect fit! No wasted space.
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Warning message */}
            {sizeWarning && (
                <div className="mt-3 bg-[#FEF3F2] text-sm text-[#8B6F47] p-3 rounded border-l-4 border-[#8B6F47]">
                    {sizeWarning}
                </div>
            )}

        </div>
    );
};

export default QuiltSetup;
